import { Grid, useMediaQuery, useTheme } from '@material-ui/core';
import { useMemo } from 'react';

// Data Layer
import {
  defaultUnitDriver,
  validLegalEntity,
} from 'data-layer-cr/dist/functions/quotations';

// Design system
import { PaddedContent } from 'design-system';

// Components
import {
  CustomerDetailsCard,
  DriverDetailsCard,
  SummaryCarDetailsCard,
} from '../../../../components';
import AdditionalCoverageCard from '../../AdditionalCoveragesCard';
import CoverageDetailsCard from '../../CoverageDetailsCard';
import DeductibleSectionWrapper from '../../DeductibleSectionWrapper';

// Shared props
import DetailsContentProps from '../DetailsContentProps';

interface MultipleOptionVehicleProps extends DetailsContentProps {
  differentDriver: boolean;
}

/**
 * This is a component used to render quotations that must show multiple deductible
 * variants
 */
const MultipleOptionVehicle = ({
  quotation,
  coverageCatalogs,
  coverageCatalogResumes,
  differentDriver,
}: MultipleOptionVehicleProps): JSX.Element => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const deductibleSection = useMemo(
    () => (
      <DeductibleSectionWrapper
        data-testid="deductible-card"
        quotation={quotation}
        coverageCatalogs={coverageCatalogs}
        coverageCatalogResumes={coverageCatalogResumes}
        deductibleGridSize={4}
      />
    ),
    [quotation, coverageCatalogs, coverageCatalogResumes],
  );

  const endorsementSection = useMemo(
    () => (
      <AdditionalCoverageCard
        data-testid="endorsement-card"
        quotation={quotation}
        coverages={coverageCatalogs}
      />
    ),
    [quotation, coverageCatalogs],
  );

  const driver = useMemo(() => defaultUnitDriver(quotation), [quotation]);

  // only display driverDetailsCard if driver and customers are different or customerType is Legal Entity
  const displayDriverCard = useMemo(
    () => differentDriver || quotation.customerType === 'J',
    [differentDriver, quotation],
  );

  return (
    <PaddedContent data-testid="content">
      <Grid
        container
        spacing={2}
        alignItems="flex-start"
        justifyContent="center"
      >
        {/* On desktop, the deductible layout should display on top of the information */}
        {!isMobile && (
          <Grid data-testid="deductible-desktop" item xs={12}>
            {deductibleSection}
          </Grid>
        )}

        {/* The main content */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {/* Customer information */}
            <Grid item xs={12} md={4}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <CustomerDetailsCard
                    data-testid="customer-section"
                    customerType={quotation.customerType}
                    customer={quotation.customer}
                    legalEntity={validLegalEntity(quotation)}
                  />
                </Grid>
                {displayDriverCard && (
                  <Grid item xs={12}>
                    <DriverDetailsCard
                      data-testid="driver-section"
                      firstName={driver.firstName}
                      lastName={driver.lastName}
                      gender={driver.gender}
                      birthdate={driver.birthday}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
            {/* Car and Endorsement information */}
            <Grid item xs={12} md={4}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <SummaryCarDetailsCard
                    data-testid="car-section"
                    quotationSummary={quotation.summary}
                  />
                </Grid>
                {!isMobile && (
                  <Grid data-testid="endorsement-desktop" item xs={12}>
                    {endorsementSection}
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12} md={4}>
              <CoverageDetailsCard
                data-testid="coverages-card"
                quotation={quotation}
                coverages={coverageCatalogs}
                coverageResumes={coverageCatalogResumes}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* On mobile, the layout should display below the information */}
        {isMobile && (
          <>
            <Grid data-testid="deductible-mobile" item xs={12}>
              {deductibleSection}
            </Grid>
            <Grid data-testid="endorsement-mobile" item xs={12}>
              {endorsementSection}
            </Grid>
          </>
        )}
      </Grid>
    </PaddedContent>
  );
};

export default MultipleOptionVehicle;
